<script lang="ts">
	import { beforeUpdate } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { devices_config } from 'src/configs/devices';
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import ControlPositionG2V2 from 'src/components/Controls/Single/ControlPositionG2V2.svelte';
	import ControlDirectionDevicePageG2V2 from 'src/components/Controls/Single/ControlDirectionDevicePageG2V2.svelte';
	import InputsReaderVisualizer from 'src/components/Controls/Indicators/InputsReaderVisualizer.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';
	import LightAdvancedView from 'src/components/Sidepanel/DeviceViews/Light/LightAdvancedView.svelte';
	import { ControlsContainer } from '@Shelly/shelly-design-system';
	import {
		get_gen_2_component_channel_structure,
		get_mode,
		is_gen_2,
		isXMODDevice,
	} from 'src/helpers/interface_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	import { isXmodLightMainMode } from 'src/helpers/light_priority_layout';

	export let device: Device;

	const deviceConfig = devices_config?.[device?.type];
	const isGen2 = is_gen_2(device);

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	// Status and label processing
	$: _statusesRaw =
		status?.online && deviceConfig ? status_configurator(status, device, settings) : null;
	$: _statuses = (Array.isArray(_statusesRaw) ? _statusesRaw : []) as any[];
	$: labelList = _statuses.filter((e) => {
		return 'undefined' === typeof e.main && e.name !== 'state';
	});
	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	let timerLabels: Record<string, any> = {};
	let gen_1_timer = false;
	let gen_2_timer = false;
	let deviceState: any = getDeviceState(device?.id);

	// Mode detection for XMOD devices
	$: mode = device?.id ? get_mode(device, $statusStore, $settingsStore) : null;
	$: useLightPriority = isXMODDevice(device?.type) && isXmodLightMainMode(mode || device?.mode);

	// Check if LabelList should be shown (based on DeviceDrawer line 1372)
	$: shouldShowLabelList =
		!isPeripheralDevice(device?.type) &&
		device?.mode !== 'media_player' &&
		device?.category !== 'emeter' &&
		device?.category !== 'pv';

	beforeUpdate(() => {
		if (status?.online && deviceConfig) {
			// Gen 1 timer check
			if (!isGen2 && device?.category !== 'emeter') {
				gen_1_timer = status?.[device?.category + 's']?.[device?.channel]?.has_timer || false;
			}

			// Gen 2 timer check
			if (isGen2) {
				const component = get_gen_2_component_channel_structure(device);
				gen_2_timer =
					typeof status?.[component]?.timer_started_at !== 'undefined' &&
					((settings?.[component]?.auto_on && !status?.[component]?.output) ||
						(settings?.[component]?.auto_off && status?.[component]?.output));
			}
		}
		deviceState = getDeviceState(device?.id);
	});
</script>

{#if useLightPriority}
	<LightAdvancedView {device} />
{:else}
	<DeviceViewStateWrapper {device}>
		<div
			slot="staticTop"
			class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
		>
			{#if device && status !== undefined && settings !== undefined && deviceConfig !== undefined && status?.online && shouldShowLabelList}
				{#if gen_2_timer}
					{#key status[get_gen_2_component_channel_structure(device)].timer_started_at}
						<TimerRemaining
							timerKey="gen2_main"
							{device}
							{status}
							{settings}
							bind:timerLabels
						/>
					{/key}
				{:else if gen_1_timer && status?.[`${device.category}s`]?.[device.channel]?.timer_started}
					{#key status[`${device.category}s`][device.channel].timer_started}
						<TimerRemaining
							timerKey="gen1_main"
							{device}
							{status}
							{settings}
							bind:timerLabels
						/>
					{/key}
				{/if}
				<DeviceLabelList labelList={combinedLabelList} />
			{/if}
			<DeviceNotificationLabel deviceId={device?.id} />
		</div>

		<div class="flex-full-height justify-content-top" slot="content">
			{#if isXMODDevice(device?.type) && mode === 'inputs_reader'}
				<ControlsContainer color="color-0">
					<InputsReaderVisualizer
						slot="first"
						{device}
						{status}
						{settings}
						isWide
						isInCard={false}
					/>
				</ControlsContainer>
			{:else if isXMODDevice(device?.type) && mode === 'roller'}
				{@const componentStructure = get_gen_2_component_channel_structure(device)}
				{#if status?.[componentStructure]?.pos_control && typeof status?.[componentStructure]?.current_pos === 'number'}
					<ControlPositionG2V2 {device} {status} {settings} />
				{/if}
			{/if}
		</div>

		<div class="flex-full-height justify-content-end mt-s" slot="staticBottom">
			{#if isXMODDevice(device?.type) && mode === 'inputs_reader'}
				<!-- Inputs reader mode doesn't show ControlOnOff -->
			{:else if isXMODDevice(device?.type) && (mode === 'relay' || mode === 'switch')}
				<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
			{:else if isXMODDevice(device?.type) && mode === 'roller'}
				<ControlDirectionDevicePageG2V2 {device} {status} />
			{:else if isXMODDevice(device?.type) && status?.['switch:0']}
				<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
			{/if}
		</div>
	</DeviceViewStateWrapper>
{/if}
